import type { InventoryOutlookReport } from "@/lib/manufacturing/inventoryMatchClient";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import type { ColorCrosswalkCard } from "@/lib/manufacturing/colorCrosswalkPropose";

export type OrderMappingGap = NonNullable<InventoryOutlookReport["gaps"]>[number];

const CSV_HEADERS = [
  "serial",
  "use_code",
  "material_code",
  "material_name",
  "quantity",
  "reason",
  "unit_id",
  "plant_id",
  "material_use_id",
  "material_id",
  "aspects",
] as const;

const NEEDS_QUOTING = /[",\r\n]/;

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  if (!NEEDS_QUOTING.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function aspectsCell(gap: OrderMappingGap): string {
  return gap.aspects.map((row) => `${row.field}=${row.value}`).join("; ");
}

/** Selected rows win. With nothing selected, export the rows the filter is showing. */
export function orderMappingExportSelection<T>(visible: readonly T[], selected: readonly T[]): readonly T[] {
  return selected.length > 0 ? selected : visible;
}

export function orderMappingGapsToJson(gaps: readonly OrderMappingGap[]): string {
  return JSON.stringify(gaps, null, 2);
}

export function orderMappingGapsToCsv(gaps: readonly OrderMappingGap[]): string {
  const lines = [CSV_HEADERS.join(",")];
  for (const gap of gaps) {
    lines.push(
      [
        gap.serial,
        gap.use_code,
        gap.material_code,
        gap.material_name,
        gap.quantity,
        gap.reason,
        gap.unit_id,
        gap.plant_id,
        gap.material_use_id,
        gap.material_id,
        aspectsCell(gap),
      ]
        .map(escapeCsvCell)
        .join(","),
    );
  }
  return lines.join("\n") + "\n";
}

function stamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function downloadText(filename: string, text: string, type: string): void {
  if (typeof window === "undefined") return;
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const CARD_HEADERS = [
  "finish_channel",
  "suggested_color",
  "series",
  "use_codes",
  "serial_count",
  "color_choices",
] as const;

function seriesCell(card: ColorCrosswalkCard): string {
  return card.modifiers.filter((modifier) => modifier.field === "series").map((modifier) => modifier.value).join(", ");
}

export function colorCrosswalkCardsToJson(cards: readonly ColorCrosswalkCard[]): string {
  return JSON.stringify(cards, null, 2);
}

export function colorCrosswalkCardsToCsv(cards: readonly ColorCrosswalkCard[]): string {
  const lines = [CARD_HEADERS.join(",")];
  for (const card of cards) {
    lines.push(
      [
        card.finish_channel,
        `${card.source_field}=${card.source_value}`,
        seriesCell(card),
        card.use_codes.join("; "),
        card.serial_count,
        card.color_choices.map((choice) => `${choice.field}=${choice.value}`).join("; "),
      ]
        .map(escapeCsvCell)
        .join(","),
    );
  }
  return lines.join("\n") + "\n";
}

export function downloadColorCrosswalkCards(format: "json" | "csv", cards: readonly ColorCrosswalkCard[]): void {
  const name = `outlook-color-mappings-${stamp()}`;
  if (format === "csv") downloadCsv(`${name}.csv`, colorCrosswalkCardsToCsv(cards));
  else downloadText(`${name}.json`, colorCrosswalkCardsToJson(cards), "application/json");
}

export function downloadOrderMapping(format: "json" | "csv", gaps: readonly OrderMappingGap[]): void {
  const name = `outlook-order-mapping-${stamp()}`;
  if (format === "csv") downloadCsv(`${name}.csv`, orderMappingGapsToCsv(gaps));
  else downloadText(`${name}.json`, orderMappingGapsToJson(gaps), "application/json");
}
